import React, { useCallback, useState, useRef, useEffect } from 'react';
import { Console, getRoute, removeUndefineds } from '@utils';
import { useRouter } from 'next/router';
import {
  FormMainCard,
  Form,
  ContentContainer,
  SubmitButton,
  SubtitleTypography,
  TitleTypography,
  TitleContainer,
  InputDiv,
  TextInput,
  PhonePrefixSelect,
} from '@components/FormComponents';
import { BackgroundGlobalStyle, PageWrapper } from '@components/PageWrapper';
import {
  getFirebaseAuth,
  isAuthError,
  useShowFirebaseAuthError,
} from '@services/firebase';
import { useAuth } from '@hooks/useAuth';
import { useOrderState } from '@hooks/useOrderState';
import { useSegment } from '@hooks/useSegment';
import {
  AskDataTypography,
  GreyOverlay,
  IconListItemText,
  LoaderComponentContainer,
} from './styles';
import {
  MenuItem,
  ListItemText,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useMekiState } from '@hooks/useMekiState';
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  ConfirmationResult,
  signInWithCustomToken,
} from 'firebase/auth';
import {
  CreateAuthCodeMutation,
  CreateAuthCodeParams,
  CreateAuthCodeMutationResponse,
} from '@queries/authCode/mutations';
import { useMutation } from '@apollo/client';
import { ReCaptcha } from '@interfaces/grecaptcha.interface';
import {
  osName,
  osVersion,
  browserName,
  mobileModel,
} from 'react-device-detect';
import { getSentry } from '@services/sentry';
import { useOpenReplayTracking } from '@services/openreplay';
import { AlertModal, AlertModalContentProps } from '@components/Modal';
import { AxiosResponse } from 'axios';
import { client as webauthnClient } from '@passwordless-id/webauthn';
import {
  ChallengeResponse,
  UserInfo,
  UserCredentials,
  LoginVerification,
  LoginVerificationParams,
} from '@interfaces';
import { BiometricApi, UserApi } from '@services/biometricLogin';
import Image from 'next/image';
import { browserIsValidForBiometric } from '@utils/validators/biometricBrowserValidator';
import { SimpleHeader } from '@components/Header/SimpleHeader';
import { useEventTracking } from '@hooks/useEventTracking';
import { ScreenEventType } from 'src/validators/Events/Screen';
import { InteractionEventTypes } from 'src/validators/Events/Interaction';
import { AutomaticEventTypes } from 'src/validators/Events/Automatic';
import { ErrorEventTypes } from 'src/validators/Events/Error';

declare global {
  interface Window {
    recaptchaVerifier: RecaptchaVerifier;
    confirmationResult: ConfirmationResult;
    grecaptcha: ReCaptcha;
    recaptchaWidgetId: number;
  }
}

const phoneCodes = () =>
  import('./phoneCodes.json').then((mod) => mod.phoneCodes);

const device =
  mobileModel === 'none'
    ? `${osName} ${osVersion}`
    : `${mobileModel} ${osName} ${osVersion}`;

type FormInput = {
  userInput: string;
};

enum BiometricProcessType {
  IDLE = 'IDLE',
  LOADING = 'LOADING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

const TAG = '[SIGN_IN_SCREEN]';
export function NewSignIn() {
  const router = useRouter();
  const { debugTrack } = useSegment();
  const { track } = useEventTracking();
  const { trackIssue, trackEvent } = useOpenReplayTracking();
  const { firebaseUser, appUser, loading: authLoading } = useAuth();
  const { showError } = useShowFirebaseAuthError();
  const { orderUuid, setOrderUuid } = useOrderState();
  const contactWrittenEventSent = useRef<boolean>(false);

  const { setRegistryFrom, typeOfSingin, setTypeOfSingin, referralCode } =
    useMekiState();

  track(ScreenEventType.ScreenLogIn);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormInput>({ defaultValues: { userInput: '' } });
  const userInput = watch('userInput');
  const userInputRef = useRef(userInput);
  const [phonePrefix, setPhonePrefix] = useState('🇨🇱 +56');
  const [phoneCodeList, setPhoneCodeList] = useState<
    {
      name: string;
      icon: string;
      prefix: string;
    }[]
  >([
    {
      name: 'Chile',
      icon: '🇨🇱',
      prefix: '+56',
    },
  ]);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [alertModal, setAlertModal] = useState<AlertModalContentProps | null>(
    null,
  );
  const [biometricProcessStatus, setBiometricProcessStatus] =
    useState<BiometricProcessType>(BiometricProcessType.IDLE);
  const [quickDisableButton, setQuickDisableButton] = useState(false);

  const [sendAuthCode, { loading: emailLoading }] = useMutation<
    CreateAuthCodeMutationResponse,
    CreateAuthCodeParams
  >(CreateAuthCodeMutation());

  const hasGeneretedCaptcha = useRef(false);

  const generateRecaptcha = () => {
    window.recaptchaVerifier = new RecaptchaVerifier(
      'sign-in-button',
      {
        size: 'invisible',
        callback: (response: string) => {
          Console.log({
            msg: TAG + 'reCAPTCHA solved, allow signInWithPhoneNumber',
            response,
          });
        },
      },
      getFirebaseAuth(),
    );
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      event.key === 'Backspace' &&
      (userInput === '+56' || userInput === '56') &&
      typeOfSingin === 'phone'
    ) {
      setValue('userInput', '');
    }
  };

  const handleSelectChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPhonePrefix(String(e.target.value));
    },
    [setPhonePrefix],
  );

  const signInWithCustomFirebaseToken = useCallback(
    async (customToken: string, userData: UserInfo) => {
      if (customToken) {
        debugTrack('AUTH', 'sign in with custom token', {});
        try {
          const userCredential = await signInWithCustomToken(
            getFirebaseAuth(),
            customToken,
          );
          Console.log({
            msg: TAG + 'Firebase login',
            user: userCredential.user,
          });
        } catch (error) {
          debugTrack('AUTH', 'error on firebase custom token sign in', {
            email: userData.email,
            phone: userData.phone,
          });
          void getSentry().then((Sentry) => {
            Sentry.captureException(error);
          });
          void trackIssue(
            `[NEW_REGISTRY_ADITIONAL_INFORMATION_SCREEN_ISSUE]: Firebase User couldn't sign in`,
            error,
          );
          Console.log({
            msg: TAG + "Firebase User couldn't sign in",
          });
        }
      }
    },
    [debugTrack, trackIssue],
  );

  const emailSingIn = useCallback(
    async (biometricFailed?: boolean) => {
      debugTrack('AUTH', 'send code', { type: 'email', userInput });
      setRegistryFrom({
        name: '',
        lastName: '',
        email: userInput,
        phonePrefix: '',
        phone: '',
        rut: '',
        code: '',
        source: '',
      });
      try {
        const response = await sendAuthCode({
          variables: {
            params: {
              email: userInput,
              device: device,
              browser: browserName,
            },
          },
        });
        const message = response.data.createAuthCode.msg;
        if (message === 'Email send successfully') {
          void trackEvent(TAG + 'Email send successfully');
          Console.log({
            msg: TAG + 'email sent',
            message,
          });
          track(AutomaticEventTypes.SessionVerificationCodeSent, {
            type: 'email',
          });
          debugTrack('AUTH', 'sign in next', { typeOfSingin, userInput });
          void router.push(
            getRoute('verifyCode', {
              biometricFailed: biometricFailed?.toString() || 'false',
            }),
          );
          return;
        }
        if (message === 'Error sending email') {
          throw new Error('Email not sent');
        }
      } catch (error) {
        await getSentry().then((Sentry) => {
          Sentry.captureException(error);
        });
        await trackIssue('[SIGN_IN_SCREEN_ISSUE]: Error email not sent', error);
        Console.log({
          msg: TAG + 'Error; email not sent',
        });
        setAlertModal({
          title: 'No pudimos enviar el correo',
          content: 'Prueba iniciando sesión con tu número de teléfono',
        });
        debugTrack('AUTH', 'send code error', {
          type: 'email',
          userInput,
        });
        track(ErrorEventTypes.SessionVerificationCodeNotSent, {
          type: typeOfSingin as 'phone' | 'email',
          phone_number: null,
          email: userInput,
          error,
          is_auth_error: isAuthError(error),
        });
        return;
      }
    },
    [
      debugTrack,
      device,
      router,
      sendAuthCode,
      setRegistryFrom,
      track,
      trackEvent,
      trackIssue,
      typeOfSingin,
      userInput,
    ],
  );

  const phoneSingIn = useCallback(
    async (biometricFailed?: boolean) => {
      const phoneNumber = phonePrefix.split(' ')[1] + ' ' + userInput;
      debugTrack('AUTH', 'send code', { type: 'phone', phoneNumber });
      Console.log({
        msg: TAG + 'Singin con numero de teléfono',
        phoneNumber,
      });
      setRegistryFrom({
        name: '',
        lastName: '',
        email: '',
        phonePrefix: phonePrefix,
        phone: phoneNumber,
        rut: '',
        code: '',
        source: '',
      });
      if (!hasGeneretedCaptcha.current) {
        generateRecaptcha();
        hasGeneretedCaptcha.current = true;
      } else {
        if (window.recaptchaWidgetId === undefined) {
          void window.recaptchaVerifier.render().then(function (widgetId) {
            window.recaptchaWidgetId = widgetId;
            window.grecaptcha.reset(widgetId);
          });
        } else {
          window.grecaptcha.reset(window.recaptchaWidgetId);
        }
      }
      const phoneWithoutSpaces =
        phoneNumber.split(' ')[0] + phoneNumber.split(' ')[1];
      try {
        setPhoneLoading(true);
        const confirmationResult = await signInWithPhoneNumber(
          getFirebaseAuth(),
          phoneWithoutSpaces,
          window.recaptchaVerifier,
        );
        // SMS sent.
        window.confirmationResult = confirmationResult;
        void trackEvent(TAG + 'SMS send successfully');
        Console.log({
          msg: TAG + 'SMS sent',
          confirmationResult,
        });
        track(AutomaticEventTypes.SessionVerificationCodeSent, {
          type: 'phone',
        });
        debugTrack('AUTH', 'sign in next', { typeOfSingin, userInput });
        void router.push(
          getRoute('verifyCode', {
            biometricFailed: biometricFailed?.toString() || 'false',
          }),
        );
        setPhoneLoading(false);
        return;
      } catch (error) {
        debugTrack('AUTH', 'send code error', {
          type: 'phone',
          phoneNumber,
        });
        if (isAuthError(error)) {
          showError(error);
          await trackIssue(
            '[SIGN_IN_SCREEN_ISSUE]: Firebase error SMS not sent',
            error,
          );
        } else {
          await trackIssue('[SIGN_IN_SCREEN_ISSUE]: Error SMS not sent', error);
        }
        track(ErrorEventTypes.SessionVerificationCodeNotSent, {
          type: typeOfSingin as 'phone' | 'email',
          phone_number: phoneNumber,
          email: null,
          error,
          is_auth_error: isAuthError(error),
        });
        await getSentry().then((Sentry) => {
          Sentry.captureException(error);
        });
        Console.log({
          msg: TAG + 'Error; SMS not sent',
        });
        setPhoneLoading(false);
        return;
      }
    },
    [
      debugTrack,
      phonePrefix,
      router,
      setRegistryFrom,
      showError,
      track,
      trackEvent,
      trackIssue,
      typeOfSingin,
      userInput,
    ],
  );
  const simpleSignIn = useCallback(
    async (biometricFailed?: boolean) => {
      if (typeOfSingin === 'email') {
        await emailSingIn(biometricFailed);
        return;
      }
      if (typeOfSingin === 'phone') {
        await phoneSingIn(biometricFailed);
        return;
      }
      debugTrack('AUTH', 'ignore signin next button', {
        typeOfSingin,
        userInput,
        emailLoading,
        phoneLoading,
      });
    },
    [
      track,
      typeOfSingin,
      debugTrack,
      userInput,
      emailLoading,
      phoneLoading,
      emailSingIn,
      phoneSingIn,
    ],
  );

  const onSubmit = useCallback(async () => {
    track(InteractionEventTypes.ClickNext, {
      location: 'login',
    });
    if (quickDisableButton) return;
    setQuickDisableButton(true);
    if (
      (emailLoading && typeOfSingin === 'email') ||
      (phoneLoading && typeOfSingin === 'phone')
    ) {
      Console.log({
        msg: 'DEBUG Block due emailLoading',
        emailLoading,
        phoneLoading,
        typeOfSingin,
      });
      debugTrack('AUTH', 'block signin next button', {
        emailLoading,
        phoneLoading,
        userInput,
      });
      return;
    }
    const isAvailableBiometric =
      !!window.PublicKeyCredential && browserIsValidForBiometric();

    const userIdentifier =
      typeOfSingin === 'email'
        ? userInput
        : phonePrefix.split(' ')[1] + userInput;
    const { data: userData } = await UserApi.get<UserInfo>(
      `/${userIdentifier}`,
    );
    Console.log({ msg: 'DEBUG userData', userData });
    debugTrack('AUTH', 'userData', {
      userData,
      isAvailableBiometric,
    });
    const userExists = userData && userData.userId !== null;
    if (isAvailableBiometric && userExists) {
      try {
        const { data: userCredentials } =
          await BiometricApi.get<UserCredentials>(
            `/credentials/${userData.email}`,
          );
        Console.log({
          msg: 'DEBUG user credentials ids',
          userCredentials,
        });
        debugTrack('AUTH', 'user credentials ids', {
          userCredentials,
        });
        if (userCredentials?.credentialsIds.length > 0) {
          try {
            setBiometricProcessStatus(BiometricProcessType.LOADING);
            const { data: challenge } =
              await BiometricApi.get<ChallengeResponse>(
                `/challenge/${userData.email}`,
              );
            Console.log({
              msg: 'DEBUG challenge',
              challenge,
            });
            debugTrack('AUTH', 'DEBUG challenge', {
              challenge: challenge.challenge,
            });
            const login = await webauthnClient.authenticate(
              userCredentials.credentialsIds,
              challenge.challenge,
              {
                authenticatorType: 'auto',
                userVerification: 'required',
              },
            );
            Console.log({
              msg: 'DEBUG verify biometric login',
              login,
              origin: window.location.origin,
              userEmail: userData.email,
            });
            debugTrack('AUTH', 'DEBUG verify biometric login', {
              login,
              origin: window.location.origin,
              userEmail: userData.email,
            });
            track(AutomaticEventTypes.SessionBiometricLoginAttempt);
            const { data: verifyLogin } = await BiometricApi.post<
              LoginVerificationParams,
              AxiosResponse<LoginVerification>
            >(`/login`, {
              authentication: login,
              origin: window.location.origin,
              userEmail: userData.email,
            });
            Console.log({
              msg: 'DEBUG verifyLogin',
              verifyLogin,
            });
            debugTrack('AUTH', 'DEBUG verifyLogin', {
              verifyLogin,
            });
            setBiometricProcessStatus(BiometricProcessType.SUCCESS);
            track(AutomaticEventTypes.SessionBiometricLoginSuccess);
            await signInWithCustomFirebaseToken(
              verifyLogin.loginCustomToken,
              userData,
            );
          } catch (error) {
            if (error instanceof DOMException) {
              Console.log({
                msg: 'DEBUG error',
                error,
              });
              debugTrack('AUTH', 'ERROR biometric login (DOMException)', {
                error: error.message,
                userEmail: userData.email,
              });
            }
            Console.log({
              msg: 'DEBUG error on biometric login',
            });
            debugTrack('AUTH', 'ERROR  biometric login', {
              error: error,
              userEmail: userData.email,
            });
            setQuickDisableButton(false);
            track(ErrorEventTypes.SessionBiometricLogin, { error });
            setBiometricProcessStatus(BiometricProcessType.ERROR);
            setTimeout(() => {
              setBiometricProcessStatus(BiometricProcessType.IDLE);
            }, 2000);
            await simpleSignIn(true);
            return;
          }
        } else {
          setQuickDisableButton(false);
          setBiometricProcessStatus(BiometricProcessType.IDLE);
          await simpleSignIn();
          return;
        }
      } catch (error) {
        Console.log({
          msg: 'DEBUG error on biometric login',
        });
        debugTrack('AUTH', 'ERROR  biometric login', {
          error: error,
          userEmail: userData.email,
        });
        setQuickDisableButton(false);
        track(ErrorEventTypes.SessionBiometricLogin, { error });
        setBiometricProcessStatus(BiometricProcessType.ERROR);
        setBiometricProcessStatus(BiometricProcessType.IDLE);
        await simpleSignIn(true);
        return;
      }
    } else {
      setQuickDisableButton(false);
      setBiometricProcessStatus(BiometricProcessType.IDLE);
      await simpleSignIn();
      return;
    }
  }, [
    quickDisableButton,
    emailLoading,
    typeOfSingin,
    phoneLoading,
    userInput,
    phonePrefix,
    debugTrack,
    track,
    signInWithCustomFirebaseToken,
    simpleSignIn,
  ]);

  useEffect(() => {
    // void track('log in start');
    setTypeOfSingin('unset');
  }, [track, setTypeOfSingin]);

  useEffect(
    function validateEmailorPhone() {
      if (typeOfSingin === 'phone' && /^\+?56/.exec(userInputRef.current)) {
        return;
      }
      if (
        ((userInput?.length < 2 && !userInput.startsWith('+')) ||
          (userInput?.length < 3 && userInput.startsWith('+'))) &&
        typeOfSingin !== 'unset'
      ) {
        debugTrack('AUTH', 'set type of input', { userInput, type: 'unset' });
        setTypeOfSingin('unset');
        return;
      }
      if (userInput?.length < 3) return;
      const isNumber = /^\+?\d+$/.test(userInput);
      if (isNumber && typeOfSingin !== 'phone') {
        setTypeOfSingin('phone');
        debugTrack('AUTH', 'set type of input', { userInput, type: 'phone' });
        return;
      }
      if (!isNumber && typeOfSingin !== 'email') {
        setTypeOfSingin('email');
        debugTrack('AUTH', 'set type of input', { userInput, type: 'email' });
        return;
      }
    },
    [userInput, setTypeOfSingin, typeOfSingin, debugTrack],
  );

  useEffect(
    function handleAmplitudeEventOnInput() {
      if (typeOfSingin === 'unset') {
        contactWrittenEventSent.current = false;
        return;
      }

      if (!contactWrittenEventSent.current) {
        if (
          (typeOfSingin === 'phone' && userInput.length >= 9) ||
          (typeOfSingin === 'email' && userInput.length >= 5)
        ) {
          track(InteractionEventTypes.LoginContactWritten, {
            type: typeOfSingin,
          });
          contactWrittenEventSent.current = true;
        }
      }
    },
    [contactWrittenEventSent, typeOfSingin, userInput],
  );

  useEffect(
    function checkLoginAndRedirect() {
      if (authLoading) return;

      const { redirect } = router.query;
      Console.log({
        msg: TAG + 'CheckLoginAndRedirect effect: validate auth',
        firebaseUser,
        appUser,
        orderUuid,
        redirect,
        authLoading,
      });
      if (!firebaseUser) {
        Console.log({ msg: TAG + ' CheckLoginAndRedirect no firebase user' });
        return;
      }
      if (appUser) {
        Console.log({
          msg: TAG + ' Redirect from signin',
          to: (redirect as string) ?? getRoute('home'),
          redirect,
          firebaseUser,
          appUser,
          router,
        });
        // void track('log in succeed', { referralCode });
        debugTrack('AUTH', 'sign in next', { typeOfSingin, userInput });
        if (!redirect) {
          return void router.replace(
            getRoute(
              'home',
              referralCode?.length > 0 ? { codigo: referralCode } : {},
            ),
          );
        }
        return void router.replace({
          pathname: redirect as string,
          query: removeUndefineds({
            codigo: referralCode?.length > 0 ? referralCode : undefined,
          }),
        });
      }
      Console.log({
        msg: TAG + ' Redirect from signin to register cause no appuser',
        to: getRoute('register'),
        firebaseUser,
        appUser,
      });
      debugTrack('AUTH', 'sign in next', { typeOfSingin, userInput });
      void router.push(getRoute('register'));
    },
    [
      router,
      setOrderUuid,
      authLoading,
      firebaseUser,
      appUser,
      track,
      referralCode,
      debugTrack,
      typeOfSingin,
      userInput,
      setTypeOfSingin,
    ],
  );

  useEffect(
    function updateUserInputRefValue() {
      userInputRef.current = userInput;
    },
    [userInput],
  );

  return (
    <>
      <BackgroundGlobalStyle />
      <SimpleHeader homeButtonColor="white" />
      <PageWrapper disableMobilePadding>
        <FormMainCard>
          <Form onSubmit={handleSubmit(onSubmit)} noValidate id="my-form">
            <TitleContainer>
              <TitleTypography>
                ¿Cuál es tu teléfono celular o tu correo electrónico?
              </TitleTypography>
              <SubtitleTypography>
                Te enviaremos un código de verificación.
              </SubtitleTypography>
            </TitleContainer>
            <ContentContainer>
              <InputDiv>
                {typeOfSingin === 'phone' && (
                  <>
                    <div id="sign-in-button"></div>
                    <PhonePrefixSelect
                      variant="outlined"
                      size="small"
                      value={phonePrefix}
                      renderValue={(selected) => {
                        return selected;
                      }}
                      onChange={handleSelectChange}
                      onOpen={async () => {
                        const codes = await phoneCodes();
                        setPhoneCodeList(codes);
                      }}
                    >
                      {phoneCodeList?.map((option) => (
                        <MenuItem
                          value={`${option.icon} ${option.prefix}`}
                          key={option.name}
                        >
                          <IconListItemText>{option.icon}</IconListItemText>
                          <ListItemText>{option.name}</ListItemText>
                          <Typography variant="body2" color="text.secondary">
                            {option.prefix}
                          </Typography>
                        </MenuItem>
                      ))}
                    </PhonePrefixSelect>
                  </>
                )}
                <TextInput
                  type={'email'}
                  {...register('userInput', {
                    required: 'Debes ingresar tu correo o teléfono',
                    onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                      setValue(
                        'userInput',
                        event.target.value.toLowerCase().replace(/\s/g, ''),
                      ),
                    validate: (value) => {
                      if (typeOfSingin == 'phone') {
                        if (phonePrefix.split(' ')[1] === '+56') {
                          if (value.length < 9) {
                            return 'Teléfono incompleto';
                          }
                          if (value.length > 9) {
                            return 'Teléfono muy largo';
                          } else {
                            if (
                              phonePrefix.split(' ')[1].length + value.length >
                              15
                            ) {
                              return 'Teléfono muy largo';
                            }
                          }
                        } else {
                          if (
                            phonePrefix.split(' ')[1].length + value.length >
                            15
                          ) {
                            return 'Teléfono muy largo';
                          }
                        }
                      } else {
                        if (
                          /^([a-zA-Z0-9_\-+.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,5})$/.exec(
                            userInput,
                          )
                        ) {
                          return true;
                        } else {
                          return 'Este email está incompleto 🤷‍♀';
                        }
                      }
                    },
                  })}
                  label={
                    typeOfSingin === 'unset'
                      ? 'Ingresa tu teléfono o correo'
                      : typeOfSingin === 'email'
                      ? 'Correo electrónico'
                      : 'Número de teléfono'
                  }
                  value={
                    typeOfSingin === 'phone'
                      ? userInput.replace(/^\+?56/, '')
                      : userInput
                  }
                  error={Boolean(errors.userInput?.message)}
                  helperText={errors.userInput?.message ?? ' '}
                  onKeyDown={handleKeyDown}
                />
              </InputDiv>
              <SubmitButton
                type="submit"
                form="my-form"
                disabled={
                  quickDisableButton ||
                  authLoading ||
                  biometricProcessStatus === BiometricProcessType.LOADING ||
                  (phoneLoading && typeOfSingin === 'phone') ||
                  (emailLoading && typeOfSingin === 'email')
                }
              >
                Siguiente
              </SubmitButton>
            </ContentContainer>
          </Form>
        </FormMainCard>
      </PageWrapper>
      {alertModal && (
        <AlertModal
          open
          title={alertModal.title}
          setOpen={(open) => !open && setAlertModal(null)}
          content={alertModal.content}
          actions={[
            {
              text: 'Ok',
              onActionClick: () => setAlertModal(null),
              variant: 'contained',
            },
          ]}
        ></AlertModal>
      )}
      {(phoneLoading || emailLoading) && (
        <GreyOverlay>
          <LoaderComponentContainer>
            <AskDataTypography>
              {phoneLoading && typeOfSingin === 'phone'
                ? 'Enviando SMS...'
                : 'Enviando correo...'}
            </AskDataTypography>
            <CircularProgress size={30} color="info" />
          </LoaderComponentContainer>
        </GreyOverlay>
      )}
      {biometricProcessStatus !== BiometricProcessType.IDLE && (
        <GreyOverlay>
          <LoaderComponentContainer>
            <AskDataTypography>
              {biometricProcessStatus === BiometricProcessType.LOADING
                ? 'Solicitando biometría...'
                : biometricProcessStatus === BiometricProcessType.SUCCESS
                ? 'Iniciando sesión...'
                : 'Error al obtener biometría'}
            </AskDataTypography>
            {(biometricProcessStatus === BiometricProcessType.LOADING ||
              biometricProcessStatus === BiometricProcessType.SUCCESS) && (
              <CircularProgress size={30} color="info" />
            )}
            {biometricProcessStatus === BiometricProcessType.ERROR && (
              <Image
                src="/assets/images/warningIcon.png"
                alt="Check"
                width={30}
                height={30}
              />
            )}
          </LoaderComponentContainer>
        </GreyOverlay>
      )}
    </>
  );
}
